import React, { useEffect, useState } from "react";
import { Lock, Sparkles, Loader2, ArrowUpRight, ArrowDownRight, Check } from "lucide-react";
import { toast } from "sonner";
import api, { getHoldingsSummary } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { fmt$2 } from "@/lib/format";

// Deterministic fake shift per ticker (−5…+5 pts) — the blurred "proposal" is illustrative only.
const sampleShift = (t) => ((t.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 11) - 5);

// Non-Pro view of the Holdings Optimizer: real positions in the clear, a blurred sample proposal, and a trial/upgrade CTA.
export default function OptimizerUpgradePreview({ onUpgrade }) {
  const { user } = useAuth();
  const { sub, refresh } = useSubscription() || {};
  const [rows, setRows] = useState(null);
  const [monthly, setMonthly] = useState(0);
  const [busy, setBusy] = useState(false);
  const canTrial = !!user && !sub?.pro_trial_used;

  useEffect(() => {
    getHoldingsSummary().then((r) => {
      const ps = (r.data.positions || []).filter((p) => (p.current_value || 0) > 0).sort((a, b) => b.current_value - a.current_value).slice(0, 6);
      setRows(ps);
      setMonthly(Number(r.data.forward_monthly_income || 0));
    }).catch(() => setRows([]));
  }, []);

  const startTrial = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/start-pro-trial");
      toast.success("Pro trial active for 3 days — run the optimizer now!");
      await refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the trial");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-border overflow-hidden" data-testid="holdings-optimizer-locked">
      <PreviewTable rows={rows} />
      <div className="border-t border-border bg-secondary/[0.04] p-4 flex justify-center">
        <div className="max-w-sm w-full rounded-lg border border-secondary/40 bg-card shadow-lg p-5 text-center" data-testid="holdings-optimizer-upsell">
          <div className="w-10 h-10 rounded-full bg-secondary/15 text-secondary grid place-items-center mx-auto"><Lock className="w-5 h-5" /></div>
          <h4 className="serif text-xl mt-3">See what your holdings could pay</h4>
          <p className="text-sm text-muted-foreground mt-1">
            You earn <b className="text-foreground">{fmt$2(monthly)}/mo</b> today. Pro reweights what you own toward more income or growth, and gives you the exact trades.
          </p>
          <ul className="text-xs text-left mt-3 space-y-1">
            {["Before/after income, yield & CAGR backtest", "Whole-share buy/sell list", "You cap how far each position moves"].map((t) => (
              <li key={t} className="flex gap-2"><Check className="w-3.5 h-3.5 text-secondary shrink-0 mt-0.5" />{t}</li>
            ))}
          </ul>
          {canTrial ? (
            <Button className="w-full rounded-full mt-4" onClick={startTrial} disabled={busy} data-testid="holdings-optimizer-start-trial">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Try Pro free for 3 days
            </Button>
          ) : (
            <Button className="w-full rounded-full mt-4" onClick={onUpgrade} data-testid="holdings-optimizer-upgrade">Upgrade to Pro</Button>
          )}
          <p className="text-[11px] text-muted-foreground mt-2">{canTrial ? "No card needed · keeps your current plan after the trial" : "Cancel anytime"}</p>
        </div>
      </div>
    </div>
  );
}

function PreviewTable({ rows }) {
  if (rows === null) return <div className="h-72 animate-pulse bg-muted/40" />;
  const total = rows.reduce((a, p) => a + p.current_value, 0) || 1;
  const list = rows.length ? rows : [{ ticker: "SCHD", current_value: 1 }, { ticker: "VYM", current_value: 1 }, { ticker: "O", current_value: 1 }];
  return (
    <table className="w-full text-sm" data-testid="holdings-optimizer-preview-table">
      <thead className="bg-muted/50">
        <tr className="mono text-[10px] uppercase tracking-widest text-muted-foreground text-left">
          <th className="px-2 sm:px-3 py-2">Ticker</th><th className="px-2 sm:px-3 py-2 text-right">Now</th>
          <th className="px-3 py-2 text-right"><span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" />Proposed</span></th><th className="px-3 py-2">Action</th>
        </tr>
      </thead>
      <tbody>
        {list.map((p) => {
          const w = (p.current_value / total) * 100;
          const d = sampleShift(p.ticker);
          return (
            <tr key={p.ticker} className="border-t border-border">
              <td className="px-3 py-2 font-semibold">{p.ticker}</td>
              <td className="px-3 py-2 text-right mono">{w.toFixed(1)}%</td>
              <td className="px-3 py-2 text-right mono blur-[5px] select-none" aria-hidden>{Math.max(0, w + d).toFixed(1)}%</td>
              <td className="px-3 py-2 blur-[5px] select-none" aria-hidden>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap ${d >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {d >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{d >= 0 ? "BUY" : "SELL"} {Math.abs(d) * 3 + 2} sh
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
